'use strict';

const howToTabs = {
    elements: {
        $document: $(document),
    },

    selectors: {
        holders: '.js-how-to__data',
        tabsRoot: '.tabs.js-tabs',
        navList: '.js-tabs-nav-list',
        content: '.js-tabs__content',
        panelInner: '.tabs__panel-inner',
        proSection: '.how-to__pro-section',
        proTitle: '.how-to__pro-section--title',
        proContent: '.how-to__pro-section--content'
    },

    settings: {
        stripManualNumbers: true, // Whether to remove manual numbers from <p> (like "1-", "2.", "03)")
        defaultBgColor: '#e4f99b', // fallback background color
        defaultHighlightColor: '#0303d6', // fallback highlight color
    },

    init() {
        this.bindDocumentReady();
    },

    bindDocumentReady() {
        this.elements.$document.ready(() => {
            this.initTabs();
        });
    },

    /**
     * Initialize How-To tabs and pro tip section from JSON.
     * Sets colors, mounts tabs if multiple sections, hides container if invalid/empty.
     */
    initTabs() {
        const self = this;
        const $holders = $(self.selectors.holders);
        if (!$holders.length) return;

        $holders.each(function () {
            const $holder = $(this);
            const jsonStr = $holder.attr('data-howto-json');

            let parsed;
            try {
                parsed = JSON.parse(jsonStr);
            } catch (e) {
                // Invalid JSON -> hide everything
                $holder.closest('.how-to').hide();
                return;
            }

            // If JSON is empty, hide container
            if (!parsed || (!parsed.sections?.length && !parsed.proTipTitle && !parsed.proTipContent)) {
                $holder.closest('.how-to').hide();
                return;
            }

            // Handle pro tip section separately
            const $howToContainer = $holder.closest('.how-to');
            const $proSection = $howToContainer.find(self.selectors.proSection);

            if (parsed.proTipTitle || parsed.proTipContent) {
                if ($proSection.length) {
                    $proSection.find(self.selectors.proTitle).html(parsed.proTipTitle || '');
                    $proSection.find(self.selectors.proContent).html(parsed.proTipContent || '');
                    $proSection.show();
                }
            } else if ($proSection.length) {
                $proSection.hide();
            }

            // Set CSS variables for background and highlight colors
            const bgColor = parsed.bgColor || self.settings.defaultBgColor;
            const highlightColor = parsed.highlightColor || self.settings.defaultHighlightColor;
            $howToContainer[0].style.setProperty('--howto-bgcolor', bgColor);
            $howToContainer[0].style.setProperty('--howto-color', highlightColor);

            const sections = parsed.panels || parsed.sections || [];
            if (!sections.length) return;

            const $tabsRoot =
                $holder.siblings(self.selectors.tabsRoot).first()
                || $holder.closest('.how-to').find(self.selectors.tabsRoot).first();

            if (!$tabsRoot.length) return;

            self.mountTabs($tabsRoot, sections);
        });
    },

    /**
     * Convert a string to a safe ID (slug) for ARIA attributes / tab linking
     * @param {string} text
     */
    slugify(text) {
        return String(text || '')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^\w\-]+/g, '')
            .replace(/\-+/g, '-');
    },

    /**
     * Convert <p>…</p> elements into <ol><li>…</li> for semantic markup
     * Optionally removes manual numbering from paragraphs
     * @param {jQuery} $container
     * @param {Object} [opts]
     */
    paragraphsToOrderedList($container, opts) {
        const options = Object.assign({ stripManualNumbers: this.settings.stripManualNumbers }, opts || {});
        const $ps = $container.find('p');
        if (!$ps.length) return;

        const $ol = $('<ol/>', { 'class': 'how-to__steps' });

        $ps.each(function () {
            let html = $(this).html();
            if (options.stripManualNumbers) {
                html = html.replace(/^\s*\d{1,2}\s*[-.)]\s*/i, '');
            }
            const $li = $('<li/>', { 'class': 'how-to__step' }).html(html);
            $ol.append($li);
        });

        $container.empty().append($ol);
    },

    /**
     * Build the tabs markup from the sections array
     * If only one panel exists, nav list is hidden
     * @param {jQuery} $tabsRoot
     * @param {Array} sections
     */
    mountTabs($tabsRoot, sections) {
        if (!sections || !sections.length) {
            $tabsRoot.hide();
            return;
        }

        const self = this;
        const $nav = $tabsRoot.find(self.selectors.navList);
        const $content = $tabsRoot.find(self.selectors.content);

        const singlePanel = sections.length === 1;
        if (singlePanel) $nav.hide(); // hide nav if only one panel

        sections.forEach((section, index) => {
            const isActive = index === 0;
            const safeId = 'tab-' + index + '-' + self.slugify(section.panelTitle || section.title || ('panel-' + index));

            // Only create nav buttons if multiple panels
            if (!singlePanel) {
                const $li = $('<li/>', { 'class': 'tabs__nav-item' });
                const $btn = $('<button/>', {
                    'class': 'tabs__button js-tabs-nav-button' + (isActive ? ' tabs__button--active' : ''),
                    'role': 'tab',
                    'aria-selected': String(isActive),
                    'data-tab-target': safeId
                }).html(section.panelTitle || section.title || ('Panel ' + (index + 1)));
                $li.append($btn);
                $nav.append($li);
            }

            // Create panel
            const $panel = $('<div/>', {
                'class': 'tabs__panel js-tabs-panel' + (isActive ? ' tabs__panel--active' : ''),
                'data-tab-panel': safeId
            });

            const $panelInner = $('<div/>', { 'class': 'tabs__panel-inner' })
                .html(section.panelContent || section.firstText || '');
            $panel.append($panelInner);

            self.paragraphsToOrderedList($panelInner, { stripManualNumbers: true });
            $content.append($panel);
        });

        $tabsRoot.trigger('tabs:mount');
    }
};

howToTabs.init();
export default howToTabs;
